import { Stack, useMediaQuery, useTheme } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { AgentView } from "../../components/AgentView"
import Background from "../../components/background"
import { Header } from "../../components/header"
import { PostScreenLoading } from "../../components/PostScreenLoading"
import { SnackbarWithText } from "../../components/snackbar"
import { signUserOut } from "../../external/auth"
import { addCallDb } from "../../external/firestore"
import { initialAgent } from "../../types/Agent"
import { AnalyticsEvents } from "../../types/Analytics"
import { StateAction } from "../../types/State"
import { SnackbarType } from "../../types/UI"
import { getAvailableCredits, isAnonymous, isInIframe } from "../../utils/func"
import {
  ApiError,
  getAgent,
  getAgentUsage,
  getCapacity,
  getDefaultAgentId,
  getDefaultCreatorId,
} from "../../utils/general"
import { useAppContext, useAppDispatchContext } from "../Context"
import { useMixpanel } from "../hooks/useMixpanel"

export function PreScreen() {
  const [capacity, setCapacity] = useState<boolean>(false)
  const [overUsage, setOverUsage] = useState<boolean>(false)
  const [error, setError] = useState<string>("")
  const [agentExists, setAgentExists] = useState<boolean>(true)
  const [joinModalOpen, setJoinModalOpen] = useState<boolean>(false)
  const [permissionsGranted, setPermissionsGranted] = useState<boolean>(false)
  const [loginModalOpen, setLoginModalOpen] = useState<boolean>(false)
  const [isAgentReady, setIsAgentReady] = useState<boolean>(false)

  const theme = useTheme()
  const isEmbed = isInIframe()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const { state } = useLocation()
  const location = useLocation()
  const context = useAppContext()
  const { user, agent, stream, call } = context
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatchContext()
  const { logAnalyticsEvent } = useMixpanel()

  const isUserAnonymous = isAnonymous()

  useEffect(() => {
    setPermissionsGranted(false)

    if (stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop())
    }

    dispatch({ type: StateAction.end })
    dispatch({ type: StateAction.setAudioDevice, payload: null })
    dispatch({ type: StateAction.setVideoDevice, payload: null })
  }, [location.pathname, location.search])

  useEffect(() => {
    const req = async () => {
      if (isUserAnonymous) {
        dispatch({ type: StateAction.resetState })
        await signUserOut()
      }
    }
    req()
  }, [user])

  useEffect(() => {
    if (state && user) {
      navigate(`/${state}`)
    }
  }, [state, user])

  const sendAnalyticsEvent = () => {
    logAnalyticsEvent(AnalyticsEvents.AgentVisited)
  }

  useEffect(() => {
    sendAnalyticsEvent()
  }, [sendAnalyticsEvent])

  useEffect(() => {
    if (permissionsGranted && user) {
      setJoinModalOpen(true)
    }
  }, [permissionsGranted, user])

  useEffect(() => {
    if (!user) {
      setLoginModalOpen(true)
    } else {
      setLoginModalOpen(false)
    }
  }, [user])

  useEffect(() => {
    const req = async () => {
      try {
        if (user && !isUserAnonymous) {
          const res = await getCapacity()
          setCapacity(res)
        }
      } catch (error) {
        console.error("error fetching capacity", error)
      }
    }
    req()
  }, [user])

  useEffect(() => {
    const req = async () => {
      try {
        dispatch({ type: StateAction.setIsAgentVideoLoading, payload: true })

        const agentId = id || getDefaultAgentId
        if (!user) {
          setAgentExists(true)
          dispatch({ type: StateAction.setAgent, payload: initialAgent })
          dispatch({ type: StateAction.setAgentId, payload: agentId })
          dispatch({ type: StateAction.setAgentUsage, payload: null })
          dispatch({ type: StateAction.setIsAgentVideoLoading, payload: false })
          setIsAgentReady(true)
          return
        }

        const fetchedAgent = await getAgent(agentId)
        setAgentExists(true)
        dispatch({ type: StateAction.setAgent, payload: fetchedAgent })

        if (fetchedAgent.creatorId === getDefaultCreatorId) {
          dispatch({ type: StateAction.setAgentId, payload: agentId })
          dispatch({ type: StateAction.setAgentUsage, payload: null })
          dispatch({ type: StateAction.setIsAgentVideoLoading, payload: false })
          setIsAgentReady(true)
          return
        }

        const usage = await getAgentUsage(agentId)
        dispatch({ type: StateAction.setIsAgentVideoLoading, payload: false })

        if (usage.pay_as_you_go_enabled) {
          dispatch({
            type: StateAction.setAgentUsage,
            payload: {
              pay_as_you_go_enabled: usage.pay_as_you_go_enabled,
              current_usage: usage.current_usage,
              usage_limit: null,
              subscription_plan: usage.subscription_plan,
            },
          })
          setIsAgentReady(true)
          return
        } else {
          dispatch({ type: StateAction.setAgentUsage, payload: usage })
          const remainingCredits = getAvailableCredits(usage)
          setOverUsage(remainingCredits <= 0)
          setIsAgentReady(true)
        }
      } catch (error) {
        console.error("error fetching agent", error)
        if (error instanceof ApiError && error.status === 404) {
          setAgentExists(false)
          dispatch({ type: StateAction.setAgent, payload: initialAgent })
        } else {
          setError("Error checking agent availability")
        }
        setIsAgentReady(true) // Show content even if there's an error
      } finally {
        dispatch({ type: StateAction.setIsAgentVideoLoading, payload: false })
      }
    }
    req()
  }, [id, user])

  const onJoinCall = async () => {
    try {
      const agentId = id || getDefaultAgentId
      const conversationId = await addCallDb(user, agentId, agent)

      dispatch({
        type: StateAction.setCall,
        payload: {
          ...call,
          id: conversationId,
          agentId: agentId,
          chatMessages: [],
          startedAt: new Date(),
        },
      })
      dispatch({ type: StateAction.setCustomBrandingFlow, payload: false })

      logAnalyticsEvent(AnalyticsEvents.MeetingJoined, {
        call_id: conversationId,
      })
      navigate(`/rooms/${conversationId}`)
    } catch (error) {
      console.error("error joining call", error)
      setError("Error joining call: " + error)
    }
  }

  return (
    <Stack
      direction={"column"}
      position={"relative"}
      height={"100vh"}
      width={"100vw"}
      overflow={"hidden"}
    >
      {!isEmbed && <Header />}
      <SnackbarWithText
        open={error !== ""}
        severity={SnackbarType.error}
        text={error}
        handleClose={() => setError("")}
      />
      {!isAgentReady ? (
        <PostScreenLoading />
      ) : (
        <Stack
          direction={"column"}
          spacing={2}
          sx={{
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            width: "100%",
            height: isEmbed || isMobile ? "100vh" : "calc(100vh - 85px)", // Adjust height based on embed mode
            px: isEmbed ? 0 : { xs: 1, sm: 2, md: 2, lg: 3, xl: 4 },
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
          }}
        >
          <AgentView
            agent={agent}
            permissionsGranted={permissionsGranted}
            setPermissionsGranted={setPermissionsGranted}
            onJoinCall={onJoinCall}
            isUserAnonymous={isUserAnonymous}
            joinMeetingModalProps={{
              open: joinModalOpen,
              onClose: () => setJoinModalOpen(false),
            }}
            loginModalProps={{
              open: loginModalOpen,
              onClose: () => setLoginModalOpen(false),
            }}
            isEmbed={isEmbed}
            agentExists={agentExists}
          />
        </Stack>
      )}
      <Background />
    </Stack>
  )
}
